import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Check, Languages, PencilLine } from "lucide-react";
import { toast } from "sonner";
import { TRANSLATION_KINDS, type TranslationKind } from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckboxField, Input, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { errorText, Mono, PageHeader, Spinner } from "@/components/common";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Values = Record<string, string | null>;
interface Row {
  id: string;
  code: string;
  context: string | null;
  isActive: boolean;
  values: Record<string, Values>;
}
interface ListData {
  kind: TranslationKind;
  fields: string[];
  locales: { code: string; name: string }[];
  rows: Row[];
}
interface Summary {
  locales: { code: string; name: string }[];
  kinds: { kind: TranslationKind; total: number; translated: Record<string, number> }[];
}

const KIND_LABEL: Record<TranslationKind, string> = {
  category: "Kategoriler",
  service: "Hizmetler",
  question: "Form soruları",
  option: "Form seçenekleri",
  region: "Bölgeler",
  locality: "Yerleşim yerleri",
};
const FIELD_LABEL: Record<string, string> = {
  name: "Ad",
  label: "Etiket",
  description: "Açıklama",
  shortDescription: "Kısa açıklama",
  helpText: "Yardım metni",
  placeholder: "Örnek metin (kutu içinde)",
  localIntro: "Yerel tanıtım",
};
const LONG = new Set(["description", "localIntro", "helpText"]);

async function save(kind: TranslationKind, id: string, locale: string, body: Values) {
  await api(`/v1/admin/translations/${kind}/${id}/${locale}`, { method: "PUT", body });
}

/** Satır içi: ad/etiket kutusu; değişince Kaydet (Enter da kaydeder) */
function InlinePrimary({
  kind,
  row,
  locale,
  field,
  onSaved,
}: {
  kind: TranslationKind;
  row: Row;
  locale: string;
  field: string;
  onSaved: () => Promise<unknown>;
}) {
  const saved = row.values[locale]?.[field] ?? "";
  const [v, setV] = React.useState(saved);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => setV(saved), [saved]);
  const dirty = v.trim() !== saved && v.trim() !== "";
  async function submit() {
    if (!dirty || busy) return;
    setBusy(true);
    try {
      await save(kind, row.id, locale, { [field]: v.trim() });
      await onSaved();
      toast.success("Kaydedildi.");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      className="flex min-w-56 items-center gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <Input
        aria-label={`${row.code} ${locale} ${FIELD_LABEL[field]}`}
        value={v}
        placeholder="çeviri yok"
        className={cn(!saved && "border-dashed border-warning")}
        onChange={(e) => setV(e.target.value)}
      />
      {dirty && (
        <Button type="submit" size="icon" aria-label="Kaydet" disabled={busy}>
          {busy ? <Spinner /> : <Check />}
        </Button>
      )}
    </form>
  );
}

/** Tüm alanlar (açıklama, yardım metni…): kaynak dil yanında */
function FieldsDialog({
  data,
  row,
  locale,
  reference,
  onSaved,
}: {
  data: ListData;
  row: Row;
  locale: string;
  reference: string;
  onSaved: () => Promise<unknown>;
}) {
  const init = () => Object.fromEntries(data.fields.map((f) => [f, row.values[locale]?.[f] ?? ""]));
  const [v, setV] = React.useState<Record<string, string>>(init);
  const primary = data.fields[0]!;
  return (
    <ActionDialog
      wide
      onOpen={() => setV(init())}
      trigger={
        <Button size="icon" variant="ghost" aria-label={`${row.code}: tüm alanlar`}>
          <PencilLine />
        </Button>
      }
      title={`${row.code} · ${locale}`}
      description={row.context ?? undefined}
      confirmLabel="Kaydet"
      canSubmit={Boolean(v[primary]?.trim())}
      success="Kaydedildi."
      onSubmit={async () => {
        const body: Values = {};
        for (const f of data.fields) {
          const before = row.values[locale]?.[f] ?? "";
          const now = v[f]?.trim() ?? "";
          if (now !== before) body[f] = f === primary ? now : now || null;
        }
        if (Object.keys(body).length) await save(data.kind, row.id, locale, body);
        await onSaved();
      }}
    >
      <div className="grid gap-4">
        {data.fields.map((f) => {
          const ref = row.values[reference]?.[f];
          return (
            <div key={f} className="grid gap-1.5">
              <Label htmlFor={`f-${f}`}>
                {FIELD_LABEL[f] ?? f}
                {f === primary && " (zorunlu)"}
              </Label>
              {locale !== reference && (
                <p className="text-xs text-muted-foreground">
                  {reference}: {ref || "—"}
                </p>
              )}
              {LONG.has(f) ? (
                <Textarea
                  id={`f-${f}`}
                  value={v[f] ?? ""}
                  onChange={(e) => setV({ ...v, [f]: e.target.value })}
                />
              ) : (
                <Input
                  id={`f-${f}`}
                  value={v[f] ?? ""}
                  onChange={(e) => setV({ ...v, [f]: e.target.value })}
                />
              )}
            </div>
          );
        })}
      </div>
    </ActionDialog>
  );
}

export function TranslationsPage() {
  const qc = useQueryClient();
  const [kind, setKind] = React.useState<TranslationKind>("service");
  const [locale, setLocale] = React.useState("mt");
  const [missingOnly, setMissingOnly] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const summary = useQuery({
    queryKey: ["admin-translations-summary"],
    queryFn: () => api<Summary>("/v1/admin/translations/summary"),
  });
  const list = useQuery({
    queryKey: ["admin-translations", kind],
    queryFn: () => api<ListData>(`/v1/admin/translations?kind=${kind}`),
  });
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin-translations", kind] }),
      qc.invalidateQueries({ queryKey: ["admin-translations-summary"] }),
    ]);

  const data = list.data;
  const locales = summary.data?.locales ?? data?.locales ?? [];
  const reference = locales[0]?.code ?? "en";
  const primary = data?.fields[0] ?? TRANSLATION_KINDS[kind].fields[0];
  const term = search.trim().toLowerCase();
  const rows = (data?.rows ?? []).filter(
    (r) =>
      (!missingOnly || !r.values[locale]?.[primary]) &&
      (!term ||
        [r.code, r.context, r.values[reference]?.[primary], r.values[locale]?.[primary]]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(term)),
  );

  const columns: ColumnDef<Row, unknown>[] = [
    {
      id: "code",
      header: "Kayıt",
      accessorFn: (r) => r.code,
      cell: ({ row }) => (
        <div className="min-w-40">
          <Mono>{row.original.code}</Mono>
          {row.original.context && (
            <div className="text-xs text-muted-foreground">{row.original.context}</div>
          )}
          {!row.original.isActive && (
            <Badge variant="secondary" className="mt-1">
              kapalı
            </Badge>
          )}
        </div>
      ),
    },
    ...(locale !== reference
      ? [
          {
            id: "reference",
            header: locales.find((l) => l.code === reference)?.name ?? reference,
            cell: ({ row }) => (
              <span className="text-sm">
                {row.original.values[reference]?.[primary] ?? (
                  <span className="text-destructive">eksik</span>
                )}
              </span>
            ),
          } satisfies ColumnDef<Row, unknown>,
        ]
      : []),
    {
      id: "target",
      header: locales.find((l) => l.code === locale)?.name ?? locale,
      cell: ({ row }) => (
        <InlinePrimary
          kind={kind}
          row={row.original}
          locale={locale}
          field={primary}
          onSaved={refresh}
        />
      ),
    },
    {
      id: "more",
      header: "",
      cell: ({ row }) =>
        data && (
          <div className="flex justify-end">
            <FieldsDialog
              data={data}
              row={row.original}
              locale={locale}
              reference={reference}
              onSaved={refresh}
            />
          </div>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Çeviriler"
        description="Katalog ve yer adlarının her dildeki metni. Eksik çeviri müşteriye İngilizce görünür. Maltaca metinler anadili konuşan biri tarafından kontrol edilmeli."
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(summary.data?.kinds ?? []).map((k) => {
          const done = k.translated[locale] ?? 0;
          const pct = k.total ? Math.round((done / k.total) * 100) : 100;
          return (
            <button
              key={k.kind}
              type="button"
              onClick={() => setKind(k.kind)}
              className="text-left"
              aria-pressed={kind === k.kind}
            >
              <Card
                className={cn(
                  "p-4 transition-colors hover:bg-accent/40",
                  kind === k.kind && "ring-2 ring-ring",
                )}
              >
                <div className="flex items-center justify-between text-sm font-medium">
                  {KIND_LABEL[k.kind]}
                  <span className="tabular text-muted-foreground">
                    {done}/{k.total}
                  </span>
                </div>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full", pct === 100 ? "bg-success" : "bg-primary")}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <NativeSelect
          aria-label="Tür"
          value={kind}
          onChange={(e) => setKind(e.target.value as TranslationKind)}
        >
          {(Object.keys(KIND_LABEL) as TranslationKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </NativeSelect>
        <div className="flex items-center gap-2">
          <Languages className="size-4 text-muted-foreground" />
          <NativeSelect aria-label="Dil" value={locale} onChange={(e) => setLocale(e.target.value)}>
            {locales.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} ({l.code})
              </option>
            ))}
          </NativeSelect>
        </div>
        <Input
          aria-label="Ara"
          placeholder="Kod ya da metin ara"
          className="w-56"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <CheckboxField
          label="Yalnızca eksikler"
          checked={missingOnly}
          onChange={(e) => setMissingOnly(e.target.checked)}
        />
      </div>
      <DataTable
        columns={columns}
        data={data ? rows : undefined}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        empty={missingOnly ? "Bu dilde eksik çeviri yok." : "Kayıt yok."}
      />
    </>
  );
}
